import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { getArchives } from '@/lib/api'
import type { Archive } from '@/types'
import { SmartImage } from '../ui/SmartImage'

const siteLinks = [
  { name: 'Podcasts', path: '/podcasts' },
  { name: 'YouTube', path: '/conversations' },
  { name: 'Curators', path: '/curators' },
  { name: 'Artifacts', path: '/artifacts' },
]

const aboutLinks = [
  { name: 'About', path: '/about' },
  { name: 'Support', path: '/support' },
  { name: 'Contact', path: '/contact' },
]

export default function Footer() {
  const [archives, setArchives] = useState<Archive[]>([])
  useEffect(() => {
    getArchives().then(setArchives).catch(() => setArchives([]))
  }, [])

  return (
    <footer className="bg-archive-charcoal border-t border-archive-stone/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <div className="w-12 h-12 rounded-full overflow-hidden border border-archive-gold/30 group-hover:border-archive-gold transition-colors">
                <SmartImage
                    src="/images/archive-network-seal.png"
                    alt="The Archive Network"
                    sizes="54px"
                    width={54}
                    height={54}
                    loading="eager"
                    className="w-full h-full object-cover"
                  />
              </div>
              <span className="font-display text-archive-cream text-lg tracking-wide">
                The Archive Network
              </span>
            </Link>
            <p className="mt-4 text-sm text-archive-muted leading-relaxed">
              Preserving and presenting humanity's collective memory through
              curated archives, scholarly podcasts, and documentary exploration.
            </p>
          </div>

          {/* Archives */}
          <div>
            <h3 className="font-display text-archive-cream small-caps text-sm mb-4">
              The Archives
            </h3>
            <ul className="space-y-2">
              {archives.map((a) => (
                <li key={a.key}>
                  <a
                    href={a.site_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-archive-muted hover:text-archive-gold transition-colors"
                  >
                    {a.name.replace(/^The /, '')}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Discover */}
          <div>
            <h3 className="font-display text-archive-cream small-caps text-sm mb-4">
              Discover
            </h3>
            <ul className="space-y-2">
              {siteLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-archive-muted hover:text-archive-gold transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About & Support */}
          <div>
            <h3 className="font-display text-archive-cream small-caps text-sm mb-4">
              About & Support
            </h3>
            <ul className="space-y-2">
              {aboutLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-archive-muted hover:text-archive-gold transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-archive-stone/30">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3 text-xs text-archive-muted">
              <p>
                © {new Date().getFullYear()} The Archive Network. Powered by AI and passion for great stories.
              </p>
              <span className="text-archive-stone">·</span>
              <Link to="/privacy" className="hover:text-archive-gold transition-colors">
                Privacy
              </Link>
              <span className="text-archive-stone">·</span>
              <Link to="/terms" className="hover:text-archive-gold transition-colors">
                Terms
              </Link>
            </div>
            <a
              href="https://jonkai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-archive-muted hover:text-archive-gold transition-colors"
            >
              <span>A project by</span>
              <img
                src="/images/jonkai-logo.svg"
                alt="Jonkai"
                width={66}
                height={20}
                loading="lazy"
                decoding="async"
                className="h-5 opacity-70 hover:opacity-100 transition-opacity"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
